// ============================================================================
// apps/sdk.ts — AppSDK: НАБОР ФУНКЦИЙ ЯДРА для приложений (SSoT/SRP).
//
// ПРИЛОЖЕНИЕ (kind='app') — самостоятельный модуль, который ОБЩАЕТСЯ С ЯДРОМ
// ТОЛЬКО ЧЕРЕЗ AppSDK (никаких прямых импортов внутренних сторов/роутера!).
// Это гарантирует:
//   - независимость (приложение разрабатывается отдельно; ядро меняется без
//     влияния на приложение, контракт версиируется);
//   - изоляцию (падение приложения/ошибки SDK НЕ роняют платформу);
//   - расширяемость (новые функции ядра добавляются НОВОЙ группой/версией,
//     старые НЕ переписываются — не плодим легаси-поддержку).
//
// ВЫЗОВЫ SDK возвращают { ok:true, value } | { ok:false, error } — ошибки
// НЕ выбрасываются наружу (обработка внутри ядра). Обработчики событий
// приложения оборачиваются (ошибка → журнал app.{code}, подписка жива).
// ОПИСАНИЕ — в справке «SDK приложений» (DevDocs, ветка с детьми) и пополняется.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useAuthStore } from '@/store/authStore';
import { settingsStore } from '@/lib/settingsStore';
import { useAppBadgesStore } from '@/lib/appBadges';
import { createLogger } from '@/lib/logger';
import { friendlyError } from '@/lib/errors';

const log = createLogger('AppSDK');

/** Версия контракта AppSDK (не версия платформы). Расширение — новые группы
 *  функций рядом (минорная версия), старые не меняются (без легаси-поддержки). */
export const APPSDK_VERSION = 1;

// ---------- Разрешения приложения (scope-based) ----------
// Манифест объявляет permissions; платформа выдаёт grantedScopes (по манифесту +
// политике). Методы SDK ПРОВЕРЯЮТ скоуп ДО вызова; нет скоупа → {ok:false,
// PERMISSION_DENIED}. Расширяется без легаси: новый скоуп = новое значение.
export type AppScope =
  | 'data:read'       // data.query (HydraQL чтение) + чтение объектов records/objects
  | 'data:write'      // создание/изменение/удаление ОБЪЕКТОВ любых типов (records/objects)
  | 'files'           // файлы: upload/list/download/thumb (FileService)
  | 'equipment'       // подключаемое оборудование (EquipmentService: драйверы/тест/команды)
  | 'ai'              // ИИ: агенты/провайдеры/инструменты/память (AiService — AskAgent)
  | 'adapters'        // адаптеры/боты/сессии/прокси (AdapterService — внешние системы)
  | 'external'        // внешние API: DaData/OSM/STT-whisper (ExternalApiService)
  | 'extensions'      // дополнения платформы (FieldExtensions/реестр возможностей)
  | 'metadata:write'  // создание/изменение/удаление МЕТАДАННЫХ (справочники/документы/
                      // регистры/типы задач-заявок/поля/формы/модули) — как конфигуратор
  | 'messenger'       // чат (ядро): отправить/получить сообщение, контекстное обсуждение
                      // объекта (getOrCreateContextThread) — РАБОТАЕТ без UI чата
  | 'jobs'            // таск-менеджер ядра: фоновые задания (enqueue/status/cancel)
  | 'billing'         // биллинг: счета/операции/доступы/тарифы/подписки (BillingService)
  | 'notify'          // тост/бейдж
  | 'settings:app'    // user_settings app.<code>.* (getSetting/setSetting)
  | 'nav'             // навигация (openObject/section/app/url/back)
  | 'events'          // emit/subscribe/observe (локальная шина UI)
  | 'app_streams'     // streams: реалтайм-топики app.<code>.* (через сервер)
  | 'expose';         // registerAction (предоставить возможности другим)

export const ALL_APP_SCOPES: AppScope[] = [
  'data:read', 'data:write', 'files', 'equipment', 'ai', 'adapters', 'external', 'extensions', 'metadata:write', 'messenger', 'jobs', 'billing',
  'notify', 'settings:app', 'nav', 'events', 'app_streams', 'expose',
];

// ---------- Capabilities (детект ФИЧ, не платформы; P1 Луны) ----------
// Приложение узнаёт ВОЗМОЖНОСТИ среды (браузер/PWA/desktop-Wails/нативный мобайл)
// по фичам, а не по isMobile/isWails. Набор среды вычисляется один раз при старте.
export type AppCapability =
  | 'fullscreen'        // полноэкранный режим (PWA standalone/desktop)
  | 'file_system'       // нативная ФС (Wails); в PWA — нет
  | 'camera'            // getUserMedia
  | 'microphone'        // getUserMedia
  | 'geolocation'       // navigator.geolocation
  | 'push'              // Web Push (secure context)
  | 'notifications'     // Notification API
  | 'video_call'        // LiveKit (media-сервис настроен)
  | 'equipment'         // подключаемое оборудование доступно (сервер)
  | 'wasm_threads'      // SharedArrayBuffer + COOP/COEP
  | 'offline_queue'     // durable offline-очередь (IndexedDB)
  | 'app_streams'       // реалтайм-шина приложения (app.<code>.*, сервер/сервисный токен)
  | 'single_window';    // одно окно (сейчас); Wails3 multi-window — будет

function detectCapabilities(): Set<AppCapability> {
  const caps = new Set<AppCapability>();
  const env = typeof navigator !== 'undefined' ? navigator : undefined;
  if (typeof window !== 'undefined') {
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
    const isWails = !!(window as any).go?.main?.App;
    if (standalone || isWails) caps.add('fullscreen');
    if (isWails) caps.add('file_system');
  }
  if (env?.mediaDevices?.getUserMedia) {
    caps.add('camera');
    caps.add('microphone');
  }
  if (env?.geolocation) caps.add('geolocation');
  const secure = typeof window !== 'undefined' && window.isSecureContext;
  if (secure && env?.serviceWorker) caps.add('push');
  if (typeof Notification !== 'undefined') caps.add('notifications');
  if (typeof SharedArrayBuffer !== 'undefined') caps.add('wasm_threads');
  caps.add('video_call'); // LiveKit отдаёт сервер при необходимости (media-settings)
  caps.add('equipment');
  caps.add('offline_queue');
  caps.add('app_streams');
  caps.add('single_window');
  return caps;
}

let cachedCaps: Set<AppCapability> | null = null;
export function appCapabilities(): Set<AppCapability> {
  if (!cachedCaps) cachedCaps = detectCapabilities();
  return cachedCaps;
}


export function isAppScope(v: string): v is AppScope {
  return (ALL_APP_SCOPES as string[]).includes(v);
}

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: string };

export type AppArea = 'full' | 'window';

export interface AppManifest {
  code: string;
  name: string;
  synonym: string;
  appKind: string;              // рендерер: 'chat' | 'default' | ...
  area: AppArea;                // чат по умолчанию full (весь воркспейс)
  apiVersion: number;
  /** Запрашиваемые разрешения (проверяются ДО каждого вызова SDK) */
  permissions: AppScope[];
}

/** Все скоупы (для системных/доверенных приложений) */
export const ALL_SCOPES_GRANTED = (): AppScope[] => [...ALL_APP_SCOPES];

type Unsub = () => void;

// ---------- Группы функций ядра (см. справку) ----------

export interface AppSdk {
  readonly manifest: AppManifest;
  /** capabilities: возможности СРЕДЫ по фичам (не платформа) — P1 Луны */
  readonly capabilities: ReadonlySet<AppCapability>;
  /** self: версия контракта + сменить область (full|window) */
  setArea(area: AppArea): void;

  /** navigation: открыть объект/раздел/URL/другое приложение; back() */
  navigation: {
    openObject(objectType: string, code: string, id?: string): Promise<ApiResult<void>>;
    openSection(code: string): Promise<ApiResult<void>>;
    openApp(code: string): Promise<ApiResult<void>>;
    openUrl(path: string): Promise<ApiResult<void>>;
    back(): void;
  };

  /** events: шина платформы (data.*, chat.* и свои app.<code>.*); автоотписка при unmount */
  events: {
    emit(topic: string, payload?: unknown): void;
    subscribe(topic: string, fn: (payload: unknown, source: string) => void): Unsub;
    observe(fn: (topic: string, payload: unknown) => void): Unsub;
  };

  /** streams: РЕАЛТАЙМ-шина приложения (B5) — топики строго app.<code>.*.
   *  publish идёт ЧЕРЕЗ СЕРВЕРНУЮ шину (событие видят сервис-приложение с
   *  сервисным токеном, другие клиенты и это приложение); subscribe — события
   *  серверной шины с АВТОПРЕФИКСОМ (event без префикса). Автоотписка при
   *  dispose. requireScope 'app_streams'. */
  streams: {
    publish(event: string, payload?: unknown): Promise<ApiResult<void>>;
    subscribe(event: string, handler: (payload: unknown, topic: string) => void): Unsub;
  };

  /** notify: тост/бейдж/системное уведомление */
  notify: {
    toast(message: string, type?: 'info' | 'success' | 'error' | 'warning', opts?: { duration?: number }): void;
    badge(count: number): void;
  };

  /** data: доступ к данным ЧЕРЕЗ SDK (права — сервер RBAC); пока ограниченный набор */
  data: {
    query<T>(hydraql: string, params?: Record<string, unknown>): Promise<ApiResult<T>>;
  };

  /** objects: ПОЛНОЕ манипулирование ЛЮБЫМИ объектами метаданных (в т.ч. теми,
   *  которых ещё НЕТ в системе — обращение по типу/коду динамически, без
   *  хардкода; сервер резолвит тип/таблицу и применяет RBAC/права объекта).
   *  objectType: 'catalog'|'document'|'register'|'task'|'order'|'entity'; code —
   *  код типа (напр. 'counterparties'); id — UUID записи.
   *  Все вызовы: {ok,value|error}; requireScope 'data:read'/'data:write'. */
  objects: {
    get(objectType: string, code: string, id: string): Promise<ApiResult<Record<string, unknown>>>;
    list(objectType: string, code: string, opts?: { filters?: Array<{ field: string; condition: string; value?: string }>; limit?: number; offset?: number }): Promise<ApiResult<{ items: Record<string, unknown>[]; total?: number }>>;
    create(objectType: string, code: string, values: Record<string, unknown>): Promise<ApiResult<{ id: string }>>;
    update(objectType: string, code: string, id: string, values: Record<string, unknown>): Promise<ApiResult<void>>;
    remove(objectType: string, code: string, id: string, permanent?: boolean): Promise<ApiResult<void>>;
  };

  /** files: файлы/хранилища/миниатюры (FileService). requireScope 'files'. */
  files: {
    upload(name: string, data: Uint8Array | Blob, opts?: { ownerId?: string; fileType?: string }): Promise<ApiResult<{ id: string; fileName: string }>>;
    list(ownerId?: string): Promise<ApiResult<Array<{ id: string; fileName: string; fileSize?: string; fileType?: string; uploadedBy?: string; uploadedAt?: string }>>>;
    download(id: string): Promise<ApiResult<{ data: Uint8Array; fileName: string }>>;
    info(id: string): Promise<ApiResult<{ id: string; fileName: string; fileSize?: string; fileType?: string; uploadedBy?: string; uploadedAt?: string }>>;
    thumb(id: string, width?: number): Promise<ApiResult<{ data: Uint8Array }>>;
    removeFile(id: string): Promise<ApiResult<void>>;
  };

  /** equipment: подключаемое оборудование (EquipmentService — драйверы АТОЛ/
   *  Ingenico/ESC-POS/JSON; ListDrivers/Test/команды). requireScope 'equipment'. */
  equipment: {
    listDrivers(): Promise<ApiResult<Array<{ id: string; name: string; version: string; description: string }>>>;
    list(): Promise<ApiResult<Array<{ id: string; name: string; type: string; status: string }>>>;
    test(id: string, command?: string): Promise<ApiResult<{ ok: boolean; result: string }>>;
  };

  /** ai: полное взаимодействие с ИИ (вся ветка: агенты/провайдеры/модели/
   *  инструменты/память/боты). AskAgent — живой диалог; память — контекст агента.
   *  requireScope 'ai'. */
  ai: {
    ask(agentId: string, message: string, opts?: { context?: Record<string, unknown> }): Promise<ApiResult<{ reply: string }>>;
    listAgents(): Promise<ApiResult<Array<{ id: string; name: string; model?: string }>>>;
    listBots(): Promise<ApiResult<Array<{ id: string; name: string; transport?: string }>>>;
  };

  /** adapters: внешние системы через ЕДИНЫЙ механизм адаптеров (AdapterService:
   *  типы/экземпляры/прокси/сессии; боты/личности на транспортах). requireScope
   *  'adapters'. */
  adapters: {
    listTypes(): Promise<ApiResult<Array<{ id: string; name: string; icon?: string }>>>;
    list(): Promise<ApiResult<Array<{ id: string; name: string; adapterType: string; enabled?: boolean }>>>;
    test(id: string): Promise<ApiResult<{ ok: boolean; result?: string }>>;
  };

  /** external: ВНЕШНИЕ API (ExternalApiService) — DaData (подсказки/поиск),
   *  OSM (гео), whisper (речь в текст). requireScope 'external'. */
  external: {
    suggestAddress(query: string): Promise<ApiResult<Array<{ value: string; raw?: unknown }>>>;
    geocode(query: string): Promise<ApiResult<Array<{ lat: number; lon: number; label?: string }>>>;
    transcribeAudio(data: Uint8Array): Promise<ApiResult<{ text: string }>>;
  };

  /** extensions: дополнения платформы (реестр возможностей — field_view/
   *  form_handler/object_handler/command; SSoT — сервер). requireScope 'extensions'. */
  extensions: {
    list(): Promise<ApiResult<Array<{ code: string; name: string; kind: string; defaultFor?: string[] }>>>;
  };

  /** metadata: ПОЛНОЕ манипулирование МЕТАДАННЫМИ — создание/изменение/удаление
   *  справочников, документов, регистров, типов задач/заявок, реквизитов (полей),
   *  форм (кастомные: объект/список/выбор), модулей объекта/формы. Требует скоуп
   *  'metadata:write' (как конфигуратор; сервер проверяет права/защиту поставочных).
   *  Метаданные SSoT — сервер; AutoApplyDDL создаёт таблицы автоматически. */
  metadata: {
    createCatalog(def: { code?: string; name: string; synonym?: string; isFolder?: boolean }): Promise<ApiResult<{ id: string }>>;
    updateCatalog(code: string, def: { name?: string; synonym?: string }): Promise<ApiResult<void>>;
    removeCatalog(code: string): Promise<ApiResult<void>>;
    createDocument(def: { code?: string; name: string; synonym?: string }): Promise<ApiResult<{ id: string }>>;
    removeDocument(code: string): Promise<ApiResult<void>>;
    createRegister(def: { code?: string; name: string; synonym?: string; kind?: string }): Promise<ApiResult<{ id: string }>>;
    removeRegister(code: string): Promise<ApiResult<void>>;
    createTaskType(def: { code?: string; name: string; synonym?: string }): Promise<ApiResult<{ id: string }>>;
    createOrderType(def: { code?: string; name: string; synonym?: string }): Promise<ApiResult<{ id: string }>>;
    /** Поле (реквизит) ЛЮБОГО объекта: entityType = catalog/document/register/task/order */
    createField(def: { entityType: string; entityCode: string; code: string; name?: string; synonym?: string; dataType?: string; refTable?: string; isRequired?: boolean }): Promise<ApiResult<{ id: string }>>;
    updateField(entityType: string, entityCode: string, code: string, def: { name?: string; synonym?: string; isRequired?: boolean }): Promise<ApiResult<void>>;
    removeField(entityType: string, entityCode: string, code: string): Promise<ApiResult<void>>;
    /** Кастомная форма (макет/модуль): formType = object/list/selection */
    createForm(def: { parentType: string; parentCode: string; formType: string; name: string; layout?: unknown; module?: string }): Promise<ApiResult<{ id: string }>>;
    removeForm(id: string): Promise<ApiResult<void>>;
    /** Модуль объекта/формы (исходник JS — русский API ядра) */
    createModule(def: { entityType: string; entityCode: string; handler: string; source: string }): Promise<ApiResult<{ id: string }>>;
    removeModule(id: string): Promise<ApiResult<void>>;
  };

  /** windows: манипулирование ОКНАМИ приложения (площадка — AppHost). */
  windows: {
    open(title: string, render: (close: () => void) => React.ReactNode, opts?: { width?: string }): void;
    close(id: string): void;
    list(): string[];
  };

  /** context: воркспейс/пользователь/тема/настройки приложения (scoped app.{code}.) */
  context: {
    getWorkspace(): { code: string; name: string };
    getCurrentUser(): { id: string; name: string } | null;
    getSetting<T>(key: string, def: T): T;
    setSetting(key: string, value: unknown): void;
  };

  /** expose: приложение ПРЕДОСТАВЛЯЕТ возможности другим (как чат — контекстные обсуждения) */
  expose: {
    registerAction(scope: string, action: { code: string; name: string; handler: (payload?: unknown) => Promise<void> | void }): void;
  };

  /** apps: МЕЖПРИЛОЖЕНЧЕСКИЕ методы — вызвать возможность другого приложения
   *  (пример: шахматы зовут через чат человека — чат регистрирует 'invite'
   *  скоуп-действие, шахматы его вызывают). Реестр действий глобальный:
   *  registerAppAction при монтировании приложения, unregister при dispose. */
  apps: {
    /** Вызвать действие другого приложения: scope.code — 'chat.invite' и т.п. */
    invoke(scope: string, actionCode: string, payload?: unknown): Promise<ApiResult<unknown>>;
  };

  /** messenger: ЧАТ как функции ЯДРА (работают БЕЗ UI чата — отправка/обсуждение/
   *  непрочитанные в ядре; UI чата — приложение поверх). requireScope 'messenger'. */
  messenger: {    /** Отправить сообщение в диалог (диалог по id). Offline — очередь ядра
     *  (chatQueue: durable IndexedDB + client_ref). Возвращает queued=true,
     *  если сообщение встало в офлайн-очередь (появится по восстановлении сети). */
    send(conversationId: string, text: string, opts?: { replyTo?: string; attachments?: string[] }): Promise<ApiResult<{ queued: boolean }>>;
    /** Получить/создать ОБСУЖДЕНИЕ объекта (контекст: type+code+id) — идемпотентно
     *  (один чат на объект). Используется ЛЮБЫМ приложением для обсуждения своего объекта. */
    getOrCreateContextThread(ctx: { objectType: string; objectCode: string; objectId: string; title?: string }): Promise<ApiResult<{ conversationId: string }>>;
    /** Непрочитанные в диалоге/контексте (для бейджей; SSoT — сервер read_by). */
    unreadCount(conversationId?: string): Promise<ApiResult<number>>;
    /** Отметить диалог прочитанным (событие chat.read — снимает бейджи у других). */
    markRead(conversationId: string): Promise<ApiResult<void>>;
    /** Пригласить ЧЕЛОВЕКА в диалог приложения (шахматы → чат): direct с пользователем
     *  + опционально сообщение-контекст. Чату не нужно быть открытым. */
    inviteHuman(userId: string, opts?: { message?: string; appContext?: string }): Promise<ApiResult<{ conversationId: string }>>;
  };

  /** jobs: ТАСК-МЕНЕДЖЕР ядра — фоновые задания (async-агент, генерация,
   *  стратегии и т.п.): enqueue → queued → running → done/error; прогресс,
   *  события task.job.{status}.{id}. requireScope 'jobs'. */
  jobs: {
    enqueue(kind: string, payload?: unknown, opts?: { title?: string }): Promise<ApiResult<{ id: string }>>;
    status(id: string): Promise<ApiResult<{ id: string; kind: string; status: string; progress: number; result: string; error: string }>>;
    list(filter?: { status?: string }): Promise<ApiResult<Array<{ id: string; kind: string; status: string; progress: number; title: string }>>>;
    cancel(id: string): Promise<ApiResult<void>>;
  };

  /** billing: БИЛЛИНГ (под-этап A) — настройки/счета/операции/доступы/тарифы/
   *  подписки. Движения пишет только ЯДРО: приложению доступны ЧТЕНИЕ и
   *  административные методы (saveAccount/grantAccess/savePlan — право
   *  проверяет сервер), операции — через модули «Биллинг.*». Выключено →
   *  {ok:false, FAILED_PRECONDITION «Биллинг не включён в базе»}.
   *  requireScope 'billing'. */
  billing: {
    getSettings(): Promise<ApiResult<{ enabled: boolean; mode: string; money: boolean; currencyRef: string; currencyDisplay: string }>>;
    listAccounts(ownerId?: string): Promise<ApiResult<unknown[]>>;
    saveAccount(account: Record<string, unknown>): Promise<ApiResult<unknown>>;
    deleteAccount(id: string): Promise<ApiResult<void>>;
    listOperations(accountId?: string, page?: number, pageSize?: number): Promise<ApiResult<unknown[]>>;
    getMyAccount(): Promise<ApiResult<unknown>>;
    grantAccess(entityId: string, scope: string, validUntil?: string): Promise<ApiResult<void>>;
    revokeAccess(entityId: string, scope: string): Promise<ApiResult<void>>;
    hasAccess(scope: string, entityId?: string): Promise<ApiResult<boolean>>;
    listPlans(): Promise<ApiResult<unknown[]>>;
    savePlan(plan: Record<string, unknown>): Promise<ApiResult<unknown>>;
    deletePlan(code: string): Promise<ApiResult<void>>;
    subscribe(planCode: string, autoRenew?: boolean, entityId?: string): Promise<ApiResult<unknown>>;
    cancelSubscription(id: string): Promise<ApiResult<void>>;
    listSubscriptions(entityId?: string): Promise<ApiResult<unknown[]>>;
  };

  /** config: настройки КОНФИГУРАЦИИ для приложения (SSoT — сервер).
   *  get(key): собственная настройка приложения (app.<code>.<key>) приоритетнее;
   *  иначе — публичное поле конфигурации (ConfigInfo: name/description/language/
   *  timezone/format_*). Недоступный ключ → {ok:false, CONFIG_KEY_NOT_AVAILABLE}
   *  (без фоллбэков; произвольные metadata_settings — по whitelist-план §3f). */
  config: {
    get<T = string>(key: string): Promise<ApiResult<T>>;
    appSetting<T = string>(key: string, def: T): T;
  };

  /** rpc: вызов БЭКЕНДА приложения (свой сервис Go/Python/Rust, backend_url из
   *  конфигуратора). Ядро проксирует POST на endpoint метаданных (без SSRF) и
   *  передаёт RBAC-контекст пользователя (X-Hydra-User-*). Требует скоуп 'nav'? —
   *  отдельный скоуп 'rpc' не нужен: backend_url задаёт админ (доверенное). */
  rpc: {
    call(method: string, body?: unknown): Promise<ApiResult<{ status: number; body: string }>>;
  };
}

/** Ошибка скоупа — приложение запросило функцию без разрешения. */
function scopeError(scope: AppScope): ApiResult<never> {
  return { ok: false, error: `PERMISSION_DENIED: требуется разрешение «${scope}»` };
}

interface AppAction { scope: string; code: string; name: string; handler: (payload?: unknown) => Promise<void> | void }

/** ГЛОБАЛЬНЫЙ реестр действий приложений (межприложенческие вызовы, SSoT):
 *  приложение регистрирует возможности (expose.registerAction), другие вызывают
 *  sdk.apps.invoke(scope, code) — «шахматы зовут через чат человека» и т.п. */
const globalAppActions = new Map<string, AppAction>();
const globalAppCleanups = new Map<string, () => void>(); // code → отписка

/** Зарегистрировать действие приложения (внутр. — при монтировании AppHost). */
function registerGlobalAction(appCode: string, scope: string, action: AppAction): void {
  const key = `${scope}:${action.code}`;
  globalAppActions.set(key, action);
  // приложение может перезарегистрировать (reload) — накопленных чистых одна
  const prev = globalAppCleanups.get(appCode);
  if (prev) prev();
  globalAppCleanups.set(appCode, () => { globalAppActions.delete(key); });
}

interface AppSdkInternal {
  code: string;
  cleanups: Unsub[];
  granted: Set<AppScope>;
  actions: Map<string, AppAction>;
  windows: Map<string, { title: string; width?: string }>;
}

function newWindowId(): string {
  return `win-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function requireScope(internal: AppSdkInternal, scope: AppScope): boolean {
  if (!internal.granted.has(scope)) {
    log.warn(`app ${internal.code}: нет разрешения ${scope}`);
    return false;
  }
  return true;
}

function wrap<T>(fn: () => Promise<T> | T, ctx: string): Promise<ApiResult<T>> {
  return Promise.resolve()
    .then(fn)
    .then((value) => ({ ok: true as const, value }))
    .catch((e: unknown) => {
      const msg = friendlyError(e instanceof Error ? e : new Error(String(e)));
      log.warn(`app sdk ${ctx}: ${msg}`);
      return { ok: false as const, error: msg };
    });
}

/** requestIdFromError — X-Request-Id из ConnectError.metadata (сквозной
 *  трейсинг, B4): заголовок ответа доступен клиенту через Expose-Headers. */
function requestIdFromError(e: unknown): string {
  const meta = (e as { metadata?: { get?: (k: string) => string | null } })?.metadata;
  if (!meta || typeof meta.get !== 'function') return '';
  return String(meta.get('x-request-id') || meta.get('X-Request-Id') || '');
}

/** Создать AppSDK для приложения. grantedScopes = permissions манифеста,
 *  пересечённые с разрешёнными политикой (по умолчанию — ровно permissions). */
export function createAppSdk(manifest: AppManifest, grantedScopes?: AppScope[]): { sdk: AppSdk; dispose: () => void } {
  const granted = new Set<AppScope>((grantedScopes || manifest.permissions).filter(isAppScope));
  const internal: AppSdkInternal = { code: manifest.code, cleanups: [], granted, actions: new Map(), windows: new Map() };

  const sdk: AppSdk = {
    manifest,
    capabilities: appCapabilities(),
    setArea(area) {
      sdk.manifest.area = area;
      log.info(`app ${manifest.code}: area → ${area}`);
    },
    navigation: {
      async openObject(objectType, code, id) {
        if (!requireScope(internal, 'nav')) return scopeError('nav');
        return wrap(async () => {
          const path = `/designer/object/${objectType}/${encodeURIComponent(code)}${id ? `?id=${encodeURIComponent(id)}` : ''}`;
          const { navigateTo } = await import('@/lib/navigate');
          navigateTo(path);
        }, 'navigation.openObject');
      },
      async openSection(sectionCode) {
        if (!requireScope(internal, 'nav')) return scopeError('nav');
        return wrap(async () => {
          const { navigateTo } = await import('@/lib/navigate');
          navigateTo(`/enterprise/section/${encodeURIComponent(sectionCode)}`);
        }, 'navigation.openSection');
      },
      async openApp(appCode) {
        if (!requireScope(internal, 'nav')) return scopeError('nav');
        return wrap(async () => {
          const { navigateTo } = await import('@/lib/navigate');
          navigateTo(`/designer/app/${encodeURIComponent(appCode)}`);
        }, 'navigation.openApp');
      },
      async openUrl(path) {
        if (!requireScope(internal, 'nav')) return scopeError('nav');
        return wrap(async () => {
          const { navigateTo } = await import('@/lib/navigate');
          navigateTo(path);
        }, 'navigation.openUrl');
      },
      back() {
        if (!requireScope(internal, 'nav')) { log.warn(`app ${internal.code}: нет разрешения nav`); return; }
        if (window.history.length > 1) window.history.back();
        else log.warn(`app ${internal.code}: нет истории назад`);
      },
    },
    events: {
      emit(topic, payload) {
        if (!requireScope(internal, 'events')) return;
        eventBusHub.emit(topic, payload);
      },
      subscribe(topic, fn) {
        if (!requireScope(internal, 'events')) return () => undefined;
        const unsub = eventBusHub.subscribe((ev) => {
          if (ev.topic !== topic) return;
          try {
            fn(ev.payload, ev.source || '');
          } catch (e: unknown) {
            log.warn(`app ${internal.code} event ${topic}: ${String((e as Error)?.message || e)}`);
          }
        });
        internal.cleanups.push(unsub);
        return unsub;
      },
      observe(fn) {
        if (!requireScope(internal, 'events')) return () => undefined;
        const unsub = eventBusHub.subscribe((ev) => {
          try {
            fn(ev.topic, ev.payload);
          } catch (e: unknown) {
            log.warn(`app ${internal.code} observe ${ev.topic}: ${String((e as Error)?.message || e)}`);
          }
        });
        internal.cleanups.push(unsub);
        return unsub;
      },
    },
    streams: {
      async publish(event, payload) {
        if (!requireScope(internal, 'app_streams')) return scopeError('app_streams');
        return wrap(async () => {
          const topic = `app.${manifest.code}.${event}`;
          await getSDK().eventBus.publish({
            topic,
            payload: payload !== undefined ? JSON.stringify(payload) : '',
            retain: false,
          } as any);
        }, 'streams.publish');
      },
      subscribe(event, fn) {
        if (!requireScope(internal, 'app_streams')) return () => undefined;
        const topic = `app.${manifest.code}.${event}`;
        const unsub = eventBusHub.subscribe((ev) => {
          if (ev.topic !== topic) return;
          try {
            fn(ev.payload, ev.topic);
          } catch (e: unknown) {
            log.warn(`app ${internal.code} stream ${topic}: ${String((e as Error)?.message || e)}`);
          }
        });
        internal.cleanups.push(unsub);
        return unsub;
      },
    },
    notify: {
      toast(message, type = 'info', opts) {
        if (!requireScope(internal, 'notify')) return;
        useToastStore.getState().showToast(message, type, { duration: opts?.duration });
      },
      badge(count) {
        if (!requireScope(internal, 'notify')) return;
        // Бейдж ПРИЛОЖЕНИЯ в доке (SSoT — appBadges store; док читает).
        // count=0 — скрыть.
        useAppBadgesStore.getState().setBadge(manifest.code, Math.max(0, Number(count) || 0));
      },
    },
    data: {
      query<T>(hydraql: string, params?: Record<string, unknown>): Promise<ApiResult<T>> {
        if (!requireScope(internal, 'data:read')) return Promise.resolve(scopeError('data:read'));
        return wrap<T>(async () => getSDK().metadata.executeHydraql({ query: hydraql, params: (params as any) || {} }) as unknown as T, 'data.query');
      },
    },
    objects: {
      async get(objectType, code, id) {
        if (!requireScope(internal, 'data:read')) return scopeError('data:read');
        return wrap(async () => {
          // Единый generic-слой (работает для ЛЮБОГО типа/таблицы, в т.ч. ещё не
          // созданных в метаданных — сервер резолвит таблицу и применяет RBAC):
          // чтение записи = listTableData с фильтром по id.
          const r = await getSDK().metadata.listTableData({
            schema: 'public', table: code,
            filters: [{ field: 'id', condition: '=', value: id }],
            pageSize: 1, page: 0,
          } as any);
          const rows = ((r as any)?.rows || []) as Array<Record<string, unknown>>;
          return rows[0] || {};
        }, 'objects.get');
      },
      async list(objectType, code, opts) {
        if (!requireScope(internal, 'data:read')) return scopeError('data:read');
        return wrap(async () => {
          const r = await getSDK().metadata.listTableData({
            schema: 'public',
            table: code,
            filters: (opts?.filters as any) || [],
            pageSize: opts?.limit || 100,
            page: opts?.offset ? Math.floor(opts.offset / 100) : 0,
          } as any);
          return { items: ((r as any)?.rows || []) as Record<string, unknown>[], total: (r as any)?.total };
        }, 'objects.list');
      },
      async create(objectType, code, values) {
        if (!requireScope(internal, 'data:write')) return scopeError('data:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createTableRow({ objectType, code, values: values as any, schema: 'public' } as any);
          return { id: String((r as any)?.id || '') };
        }, 'objects.create');
      },
      async update(objectType, code, id, values) {
        if (!requireScope(internal, 'data:write')) return scopeError('data:write');
        return wrap(async () => {
          await getSDK().metadata.updateTableRow({ objectType, code, id, values: values as any, schema: 'public' } as any);
        }, 'objects.update');
      },
      async remove(objectType, code, id, permanent) {
        if (!requireScope(internal, 'data:write')) return scopeError('data:write');
        return wrap(async () => {
          await getSDK().metadata.deleteTableRow({ objectType, code, id, permanent: !!permanent, schema: 'public' } as any);
        }, 'objects.remove');
      },
    },
    files: {
      async upload(name, data, opts) {
        if (!requireScope(internal, 'files')) return scopeError('files');
        return wrap(async () => {
          let bytes: Uint8Array;
          if (data instanceof Blob) {
            bytes = new Uint8Array(await data.arrayBuffer());
          } else {
            bytes = data;
          }
          const r = await getSDK().files.uploadFile({
            data: bytes as any, fileName: name, fileType: opts?.fileType || 'application/octet-stream',
            ownerId: opts?.ownerId || '',
          });
          return { id: String((r as any)?.id || ''), fileName: String((r as any)?.fileName || name) };
        }, 'files.upload');
      },
      async list(ownerId) {
        if (!requireScope(internal, 'files')) return scopeError('files');
        return wrap(async () => {
          const r = await getSDK().files.listFiles({ ownerId: ownerId || '' });
          return (((r as any)?.items || []) as any[]).map((x: any) => ({
            id: String(x.id || ''),
            fileName: String(x.fileName || x.file_name || ''),
            fileSize: x.fileSize != null ? String(x.fileSize) : undefined,
            fileType: String(x.fileType || ''),
            uploadedBy: x.uploadedBy != null ? String(x.uploadedBy) : undefined,
            uploadedAt: x.uploadedAt != null ? String(x.uploadedAt) : undefined,
          }));
        }, 'files.list');
      },
      async download(id) {
        if (!requireScope(internal, 'files')) return scopeError('files');
        return wrap(async () => {
          const r = await getSDK().files.downloadFile({ id });
          return { data: (r as any)?.data as Uint8Array, fileName: String((r as any)?.fileName || '') };
        }, 'files.download');
      },
      async info(id) {
        if (!requireScope(internal, 'files')) return scopeError('files');
        return wrap(async () => {
          const r = await getSDK().files.getFileInfo({ id });
          return {
            id,
            fileName: String((r as any)?.fileName || ''),
            fileSize: (r as any)?.fileSize != null ? String((r as any).fileSize) : undefined,
            fileType: String((r as any)?.fileType || ''),
            uploadedBy: (r as any)?.uploadedBy != null ? String((r as any).uploadedBy) : undefined,
            uploadedAt: (r as any)?.uploadedAt != null ? String((r as any).uploadedAt) : undefined,
          };
        }, 'files.info');
      },
      async thumb(id, width) {
        if (!requireScope(internal, 'files')) return scopeError('files');
        return wrap(async () => {
          const r = await getSDK().files.getFileThumb({ id, width: width || 256 });
          return { data: (r as any)?.data as Uint8Array };
        }, 'files.thumb');
      },
      async removeFile(id) {
        if (!requireScope(internal, 'files')) return scopeError('files');
        return wrap(async () => {
          await getSDK().files.deleteFile({ id });
        }, 'files.remove');
      },
    },
    equipment: {
      async listDrivers() {
        if (!requireScope(internal, 'equipment')) return scopeError('equipment');
        return wrap(async () => {
          const r = await getSDK().equipment.listDrivers({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({
            id: String(x.id || ''), name: String(x.name || ''), version: String(x.version || ''), description: String(x.description || ''),
          }));
        }, 'equipment.listDrivers');
      },
      async list() {
        if (!requireScope(internal, 'equipment')) return scopeError('equipment');
        return wrap(async () => {
          const r = await getSDK().equipment.listEquipment({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({
            id: String(x.id || ''), name: String(x.name || ''), type: String(x.type || ''), status: String(x.status || ''),
          }));
        }, 'equipment.list');
      },
      async test(id, command) {
        if (!requireScope(internal, 'equipment')) return scopeError('equipment');
        return wrap(async () => {
          const r = await getSDK().equipment.testEquipment({ id, command: command || '' });
          return { ok: !!(r as any)?.ok, result: String((r as any)?.result || '') };
        }, 'equipment.test');
      },
    },
    ai: {
      async ask(agentId, message, opts) {
        if (!requireScope(internal, 'ai')) return scopeError('ai');
        return wrap(async () => {
          const r = await getSDK().ai.askAgent({ agentId, message, context: (opts?.context as any) || {} } as any);
          return { reply: String((r as any)?.reply || (r as any)?.answer || '') };
        }, 'ai.ask');
      },
      async listAgents() {
        if (!requireScope(internal, 'ai')) return scopeError('ai');
        return wrap(async () => {
          const r = await getSDK().ai.listAiAgents({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({ id: String(x.id || ''), name: String(x.name || ''), model: x.model ? String(x.model) : undefined }));
        }, 'ai.listAgents');
      },
      async listBots() {
        if (!requireScope(internal, 'ai')) return scopeError('ai');
        return wrap(async () => {
          const r = await getSDK().ai.listAiBots({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({ id: String(x.id || ''), name: String(x.name || ''), transport: x.transport ? String(x.transport) : undefined }));
        }, 'ai.listBots');
      },
    },
    adapters: {
      async listTypes() {
        if (!requireScope(internal, 'adapters')) return scopeError('adapters');
        return wrap(async () => {
          const r = await getSDK().adapter.listAdapterTypes({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({ id: String(x.id || x.code || ''), name: String(x.name || ''), icon: x.icon ? String(x.icon) : undefined }));
        }, 'adapters.listTypes');
      },
      async list() {
        if (!requireScope(internal, 'adapters')) return scopeError('adapters');
        return wrap(async () => {
          const r = await getSDK().adapter.listAdapters({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({ id: String(x.id || ''), name: String(x.name || ''), adapterType: String(x.adapterType || x.adapter_type || ''), enabled: x.enabled !== false }));
        }, 'adapters.list');
      },
      async test(id) {
        if (!requireScope(internal, 'adapters')) return scopeError('adapters');
        return wrap(async () => {
          const r = await getSDK().adapter.testAdapter({ id } as any);
          return { ok: !!(r as any)?.ok, result: String((r as any)?.result || '') };
        }, 'adapters.test');
      },
    },
    external: {
      async suggestAddress(query) {
        if (!requireScope(internal, 'external')) return scopeError('external');
        return wrap(async () => {
          const r = await getSDK().geo.suggestAddress({ query });
          return (((r as any)?.items || []) as any[]).map((x: any) => ({ value: String(x?.value || ''), raw: x }));
        }, 'external.suggestAddress');
      },
      async geocode(q) {
        if (!requireScope(internal, 'external')) return scopeError('external');
        return wrap(async () => {
          const r: any = await getSDK().geo.geocode({ address: q });
          return [{ lat: Number(r?.lat || 0), lon: Number(r?.lon || 0), label: r?.displayName ? String(r.displayName) : undefined }];
        }, 'external.geocode');
      },
      async transcribeAudio(data) {
        if (!requireScope(internal, 'external')) return scopeError('external');
        // STT-whisper транскрибирует ФАЙЛ платформы (file_id): загружаем аудио
        // через файлы, затем транскрибация (два шага — единый механизм ядра).
        return wrap(async () => {
          const up = await getSDK().files.uploadFile({ data: data as any, fileName: 'voice.webm', fileType: 'audio/webm' });
          const fid = String((up as any)?.id || '');
          if (!fid) return { text: '' } as never;
          const r = await getSDK().externalApi.transcribeAudio({ fileId: fid });
          return { text: String((r as any)?.text || '') };
        }, 'external.transcribeAudio');
      },
    },
    extensions: {
      async list() {
        if (!requireScope(internal, 'extensions')) return scopeError('extensions');
        return wrap(async () => {
          const r = await getSDK().metadata.listFieldExtensions({});
          return (((r as any)?.items || []) as any[]).map((x: any) => ({
            code: String(x?.code || x?.name || ''), name: String(x?.name || ''), kind: String(x?.kind || ''),
            defaultFor: Array.isArray(x?.defaultFor) ? x.defaultFor.map(String) : x?.default_for ? String(x.default_for).split(',').map((s: string) => s.trim()) : undefined,
          }));
        }, 'extensions.list');
      },
    },
    metadata: {
      async createCatalog(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createCatalog({ code: def.code || '', name: def.name, synonym: def.synonym || '', isFolder: !!def.isFolder } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createCatalog');
      },
      async updateCatalog(code, def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.updateCatalog({ code, name: def.name || '', synonym: def.synonym || '' } as any);
        }, 'metadata.updateCatalog');
      },
      async removeCatalog(code) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.deleteCatalog({ code } as any);
        }, 'metadata.removeCatalog');
      },
      async createDocument(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createDocument({ code: def.code || '', name: def.name, synonym: def.synonym || '' } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createDocument');
      },
      async removeDocument(code) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.deleteDocument({ code } as any);
        }, 'metadata.removeDocument');
      },
      async createRegister(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createRegister({ code: def.code || '', name: def.name, synonym: def.synonym || '', kind: def.kind || '' } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createRegister');
      },
      async removeRegister(code) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.deleteRegister({ code } as any);
        }, 'metadata.removeRegister');
      },
      async createTaskType(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().task.createTaskType({ code: def.code || '', name: def.name, synonym: def.synonym || '' } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createTaskType');
      },
      async createOrderType(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().order.createOrderType({ code: def.code || '', name: def.name, synonym: def.synonym || '' } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createOrderType');
      },
      async createField(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createField({ entityType: def.entityType, entityCode: def.entityCode, code: def.code, name: def.name || '', synonym: def.synonym || def.name || def.code, dataType: def.dataType || 'text', refTable: def.refTable || '', isRequired: !!def.isRequired } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createField');
      },
      async updateField(entityType, entityCode, code, def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.updateField({ entityType, entityCode, code, name: def.name || '', synonym: def.synonym || '', isRequired: def.isRequired ?? false } as any);
        }, 'metadata.updateField');
      },
      async removeField(entityType, entityCode, code) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.deleteField({ entityType, entityCode, code } as any);
        }, 'metadata.removeField');
      },
      async createForm(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createForm({ parentType: def.parentType, parentCode: def.parentCode, formType: def.formType, name: def.name, layout: def.layout ? JSON.stringify(def.layout) : '', module: def.module || '' } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createForm');
      },
      async removeForm(id) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.deleteForm({ id } as any);
        }, 'metadata.removeForm');
      },
      async createModule(def) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          const r = await getSDK().metadata.createModule({ entityType: def.entityType, entityCode: def.entityCode, handler: def.handler, source: def.source } as any);
          return { id: String((r as any)?.id || '') };
        }, 'metadata.createModule');
      },
      async removeModule(id) {
        if (!requireScope(internal, 'metadata:write')) return scopeError('metadata:write');
        return wrap(async () => {
          await getSDK().metadata.deleteModule({ id } as any);
        }, 'metadata.removeModule');
      },
    },
    windows: {
      open(title, _render, opts) {
        const id = newWindowId();
        internal.windows.set(id, { title, width: opts?.width });
        // Хост приложения (AppHost, area='window') слушает и рендерит контент
        eventBusHub.emit(`app.${manifest.code}.window.open`, { id, title, width: opts?.width });
        return id;
      },
      close(id) {
        internal.windows.delete(id);
        eventBusHub.emit(`app.${manifest.code}.window.close`, { id });
      },
      list() {
        return Array.from(internal.windows.keys());
      },
    },
    context: {
      getWorkspace() {
        const ws = useWorkspaceStore.getState().getActive();
        return { code: ws?.code || '', name: ws?.synonym || ws?.name || '' };
      },
      getCurrentUser() {
        const u = useAuthStore.getState().user;
        return u ? { id: u.id, name: u.name || u.login || '' } : null;
      },
      getSetting(key, def) {
        if (!requireScope(internal, 'settings:app')) return def;
        return settingsStore.getJSON(`app.${manifest.code}.${key}`, def);
      },
      setSetting(key, value) {
        if (!requireScope(internal, 'settings:app')) return;
        settingsStore.setJSON(`app.${manifest.code}.${key}`, value);
      },
    },
    expose: {
      registerAction(scope, action) {
        if (!requireScope(internal, 'expose')) return;
        const full: AppAction = { ...action, scope };
        internal.actions.set(`${scope}:${action.code}`, full);
        registerGlobalAction(manifest.code, scope, full);
        log.info(`app ${manifest.code}: expose ${scope}:${action.code}`);
      },
    },
    apps: {
      async invoke(scope, actionCode, payload) {
        const key = `${scope}:${actionCode}`;
        const action = globalAppActions.get(key);
        if (!action) {
          return { ok: false, error: `APP_ACTION_NOT_FOUND: нет действия ${key} (приложение не запущено/не зарегистрировано)` };
        }
        return wrap(async () => { await action.handler(payload); }, `apps.invoke ${key}`);
      },
    },
    messenger: {
      async send(conversationId, text, opts) {
        if (!requireScope(internal, 'messenger')) return scopeError('messenger');
        return wrap(async () => {
          const myId = useAuthStore.getState().user?.id || '';
          // ЕДИНАЯ offline-отправка (chatQueue: durable IndexedDB-очередь +
          // client_ref идемпотентность) — как в UI чата. Онлайн → сервер;
          // офлайн → очередь (доставляется при восстановлении сети).
          const { sendChatMessage } = await import('@/lib/chatQueue');
          const res = await sendChatMessage({
            conversationId,
            text,
            replyTo: opts?.replyTo || '',
            attachments: opts?.attachments || [],
          } as any, myId);
          return { queued: !!res.queued };
        }, 'messenger.send');
      },
      async getOrCreateContextThread(ctx) {
        if (!requireScope(internal, 'messenger')) return scopeError('messenger');
        return wrap(async () => {
          // Один чат на объект: сначала поиск по контексту, иначе создание.
          const list = await getSDK().messenger.listConversations({ contextType: ctx.objectType, contextId: ctx.objectId });
          const items = ((list as any)?.items || []) as any[];
          if (items.length > 0) return { conversationId: String(items[0].id || '') };
          const cr = await getSDK().messenger.createConversation({
            kind: 'group', title: ctx.title || '', memberIds: [],
            contextType: ctx.objectType, contextCode: ctx.objectCode, contextId: ctx.objectId,
          } as any);
          return { conversationId: String((cr as any)?.conversation?.id || (cr as any)?.id || '') };
        }, 'messenger.getOrCreateContextThread');
      },
      async unreadCount(conversationId) {
        if (!requireScope(internal, 'messenger')) return scopeError('messenger');
        return wrap(async () => {
          if (conversationId) {
            const r = await getSDK().messenger.getConversation({ id: conversationId });
            const myId = useAuthStore.getState().user?.id || '';
            const unread = ((r as any)?.conversation?.messages || []).filter((m: any) => !(m.readBy || []).includes(myId)).length;
            return unread;
          }
          const list = await getSDK().messenger.listConversations({});
          const myId = useAuthStore.getState().user?.id || '';
          return ((list as any)?.items || []).reduce((sum: number, c: any) => sum + (Number(c.unreadCount || c.unread_count || 0)), 0);
        }, 'messenger.unreadCount');
      },
      async markRead(conversationId) {
        if (!requireScope(internal, 'messenger')) return scopeError('messenger');
        return wrap(async () => {
          await getSDK().messenger.markConversationRead({ conversationId });
        }, 'messenger.markRead');
      },
      async inviteHuman(userId, opts) {
        if (!requireScope(internal, 'messenger')) return scopeError('messenger');
        return wrap(async () => {
          // Direct с пользователем (или существующий) + приветствие-контекст приложения.
          const list = await getSDK().messenger.listConversations({ kind: 'direct' } as any);
          const mine = ((list as any)?.items || []) as any[];
          const direct = mine.find((c: any) => (c.memberIds || []).includes(userId) || c.memberIds?.length === 1 && c.memberIds?.[0] === userId);
          if (direct) {
            if (opts?.message) await getSDK().messenger.sendMessage({ conversationId: direct.id, text: opts.message } as any);
            return { conversationId: String(direct.id || '') };
          }
          const cr = await getSDK().messenger.createConversation({ kind: 'direct', title: '', memberIds: [userId], contextType: '', contextCode: '', contextId: '' } as any);
          const convId = String((cr as any)?.conversation?.id || (cr as any)?.id || '');
          if (opts?.message) await getSDK().messenger.sendMessage({ conversationId: convId, text: opts.message } as any);
          return { conversationId: convId };
        }, 'messenger.inviteHuman');
      },
    },
    rpc: {
      async call(method, body) {
        // Вызов БЭКЕНДА приложения (свой сервис Go/Python/Rust, backend_url из
        // конфигуратора) через ядро (PageService.CallAppBackend — POST, RBAC-контекст
        // передаётся ядром). Без отдельного скоупа: backend_url задаёт админ.
        // Ошибки — с request_id сквозного трейсинга (если бэкенд/ядро вернули).
        try {
          const r = await getSDK().page.callAppBackend({
            appCode: manifest.code,
            method,
            body: body === undefined ? '' : (typeof body === 'string' ? body : JSON.stringify(body)),
          } as any);
          const status = Number((r as any)?.status || 0);
          const requestId = String((r as any)?.requestId || '');
          if (status >= 500) {
            log.warn(`app ${manifest.code} rpc.call ${method}: HTTP ${status} (request_id=${requestId || '—'})`);
          }
          return { ok: true as const, value: { status, body: String((r as any)?.body || '') } };
        } catch (e: unknown) {
          const rid = requestIdFromError(e);
          const msg = friendlyError(e instanceof Error ? e : new Error(String(e)));
          log.warn(`app ${manifest.code} rpc.call ${method}: ${msg}${rid ? ` (request_id=${rid})` : ''}`);
          return { ok: false as const, error: rid ? `${msg} (request_id: ${rid})` : msg };
        }
      },
    },
    jobs: {
      async enqueue(kind, payload, opts) {
        if (!requireScope(internal, 'jobs')) return scopeError('jobs');
        return wrap(async () => {
          const r = await getSDK().jobs.enqueueJob({
            kind,
            title: opts?.title || '',
            payload: payload === undefined ? '' : JSON.stringify(payload),
            appCode: manifest.code,
          } as any);
          return { id: String((r as any)?.id || '') };
        }, 'jobs.enqueue');
      },
      async status(id) {
        if (!requireScope(internal, 'jobs')) return scopeError('jobs');
        return wrap(async () => {
          const r = await getSDK().jobs.getJob({ id } as any);
          return {
            id: String((r as any)?.id || ''),
            kind: String((r as any)?.kind || ''),
            status: String((r as any)?.status || ''),
            progress: Number((r as any)?.progress || 0),
            result: String((r as any)?.result || ''),
            error: String((r as any)?.error || ''),
          };
        }, 'jobs.status');
      },
      async list(filter) {
        if (!requireScope(internal, 'jobs')) return scopeError('jobs');
        return wrap(async () => {
          const r = await getSDK().jobs.listJobs({ status: filter?.status || '', appCode: '', limit: 100, offset: 0 } as any);
          return (((r as any)?.items || []) as any[]).map((j: any) => ({
            id: String(j.id || ''), kind: String(j.kind || ''), status: String(j.status || ''),
            progress: Number(j.progress || 0), title: String(j.title || ''),
          }));
        }, 'jobs.list');
      },
      async cancel(id) {
        if (!requireScope(internal, 'jobs')) return scopeError('jobs');
        return wrap(async () => {
          await getSDK().jobs.cancelJob({ id } as any);
        }, 'jobs.cancel');
      },
    },
    config: {
      async get<T = string>(key: string) {
        // 1) Своя настройка приложения (app.<code>.<key>) — приоритет.
        const own = settingsStore.getJSON(`app.${manifest.code}.${key}`, undefined);
        if (own !== undefined) return { ok: true as const, value: own as T };
        // 2) Публичные поля конфигурации (ConfigInfo) — известные ключи.
        const info = await wrap<Record<string, unknown>>(async () => getSDK().config.getConfigInfo({}) as unknown as Record<string, unknown>, 'config.info');
        if (info.ok && info.value) {
          const v = info.value[key];
          if (v !== undefined) return { ok: true as const, value: v as T };
        }
        return { ok: false as const, error: `CONFIG_KEY_NOT_AVAILABLE: ключ «${key}» недоступен приложению` };
      },
      appSetting<T = string>(key: string, def: T) {
        return settingsStore.getJSON<T>(`app.${manifest.code}.${key}`, def);
      },
    },
    // БИЛЛИНГ (под-этап A): чтение + административные методы (права — сервер).
    // Операции (deposit/debit/hold/...) приложению НЕ доступны — их пишет
    // только ядро (модули «Биллинг.*»); выключено → FAILED_PRECONDITION.
    billing: {
      async getSettings() {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.getBillingSettings({});
          const st = r?.settings || {};
          return {
            enabled: !!st.enabled, mode: String(st.mode || 'manual'), money: !!st.money,
            currencyRef: String(st.currencyRef || ''), currencyDisplay: String(st.currencyDisplay || ''),
          };
        }, 'billing.getSettings');
      },
      async listAccounts(ownerId) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.listBillingAccounts({ ownerId: ownerId || '' });
          return (r?.items || []) as unknown[];
        }, 'billing.listAccounts');
      },
      async saveAccount(account) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.saveBillingAccount(account as any);
          return r as unknown;
        }, 'billing.saveAccount');
      },
      async deleteAccount(id) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => { await getSDK().billing.deleteBillingAccount({ id }); }, 'billing.deleteAccount');
      },
      async listOperations(accountId, page, pageSize) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.listBillingOperations({
            accountId: accountId || '', page: page || 0, pageSize: pageSize || 100,
          });
          return (r?.items || []) as unknown[];
        }, 'billing.listOperations');
      },
      async getMyAccount() {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.getMyBillingAccount({});
          return { account: r?.account, balance: r?.balance, holds: r?.holds || [], operations: r?.operations || [] } as unknown;
        }, 'billing.getMyAccount');
      },
      async grantAccess(entityId, scope, validUntil) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          await getSDK().billing.grantBillingAccess({ entityId, scope, validUntil: validUntil || '' });
        }, 'billing.grantAccess');
      },
      async revokeAccess(entityId, scope) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          await getSDK().billing.revokeBillingAccess({ entityId, scope });
        }, 'billing.revokeAccess');
      },
      async hasAccess(scope, entityId) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.hasBillingAccess({ entityId: entityId || '', scope });
          return !!r?.granted;
        }, 'billing.hasAccess');
      },
      async listPlans() {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.listBillingPlans({});
          return (r?.items || []) as unknown[];
        }, 'billing.listPlans');
      },
      async savePlan(plan) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.saveBillingPlan(plan as any);
          return r as unknown;
        }, 'billing.savePlan');
      },
      async deletePlan(code) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => { await getSDK().billing.deleteBillingPlan({ code }); }, 'billing.deletePlan');
      },
      async subscribe(planCode, autoRenew, entityId) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.subscribeBillingPlan({
            entityId: entityId || '', planCode, autoRenew: !!autoRenew,
          });
          return r as unknown;
        }, 'billing.subscribe');
      },
      async cancelSubscription(id) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => { await getSDK().billing.cancelBillingSubscription({ id }); }, 'billing.cancelSubscription');
      },
      async listSubscriptions(entityId) {
        if (!requireScope(internal, 'billing')) return scopeError('billing');
        return wrap(async () => {
          const r: any = await getSDK().billing.listBillingSubscriptions({ entityId: entityId || '' });
          return (r?.items || []) as unknown[];
        }, 'billing.listSubscriptions');
      },
    },
  };

  const dispose = () => {
    for (const unsub of internal.cleanups) {
      try { unsub(); } catch (e: unknown) { log.warn(`app ${manifest.code} dispose: ${String((e as Error)?.message || e)}`); }
    }
    internal.cleanups.length = 0;
  };

  return { sdk, dispose };
}

// ---------- Реестр приложений (рендереры по appKind) ----------

export { registerAppRenderer, getAppRenderer, getAppRendererByCode, listAppRenderers, resolveAppManifest } from './renderers';
export type { AppRenderer } from './renderers';
